import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

import {
  cacheAgentNumber,
  getCachedAgentNumber,
  resolveAgentNumber,
} from "../utils/agentNumber";

export interface AgentNumberContextValue {
  /** Agent the number belongs to — consumers must match it before trusting `agentNumber`. */
  agentId: string;
  /** Resolved agent number; "" while loading and when it could not be resolved. */
  agentNumber: string;
  isLoading: boolean;
}

const AgentNumberContext = createContext<AgentNumberContextValue | null>(null);

/**
 * Agent number resolved once for the current agent screen.
 *
 * Returns `null` outside a provider, so consumers keep whatever fallback they
 * already had — mounting the provider is an optimisation, never a requirement.
 */
export const useAgentNumber = (): AgentNumberContextValue | null =>
  useContext(AgentNumberContext);

interface AgentNumberProviderProps {
  /** Agent in the URL. Everything is keyed off this, never off in-memory state. */
  agentId?: string;
  /**
   * Number the screen already has (list row, redux, session state). Pass it only
   * once it is confirmed to belong to `agentId`; the persisted agent state can
   * belong to a previously visited agent.
   */
  seedAgentNumber?: string;
  children: React.ReactNode;
}

/**
 * Resolves the agent number a single time for the agent tab screens and shares
 * it with everything underneath — the header badge and the import template
 * downloads, which would otherwise each resolve it again on click.
 */
export const AgentNumberProvider: React.FC<AgentNumberProviderProps> = ({
  agentId = "",
  seedAgentNumber = "",
  children,
}) => {
  // Seed the first render too, so an already known number never flashes a skeleton.
  const knownOnMount = seedAgentNumber || getCachedAgentNumber(agentId);
  const [agentNumber, setAgentNumber] = useState<string>(knownOnMount);
  const [isLoading, setIsLoading] = useState<boolean>(!!agentId && !knownOnMount);

  useEffect(() => {
    if (!agentId) {
      setAgentNumber("");
      setIsLoading(false);
      return;
    }

    const known = seedAgentNumber || getCachedAgentNumber(agentId);
    if (known) {
      // Share it with the download flow so it skips the lookup as well.
      cacheAgentNumber(agentId, known);
      setAgentNumber(known);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setAgentNumber("");
    setIsLoading(true);
    resolveAgentNumber(agentId).then((resolved) => {
      if (cancelled) return;
      setAgentNumber(resolved);
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [agentId, seedAgentNumber]);

  const value = useMemo(
    () => ({ agentId, agentNumber, isLoading }),
    [agentId, agentNumber, isLoading]
  );

  return (
    <AgentNumberContext.Provider value={value}>
      {children}
    </AgentNumberContext.Provider>
  );
};

export default AgentNumberContext;
